import React, { useState, useEffect, useMemo } from 'react';
import { getDatabase, ref, onValue } from 'firebase/database';
import { app } from '../firebase/config';
import { CSVLink } from 'react-csv';
import { FaDownload, FaFileInvoice } from 'react-icons/fa';

const GSTReport = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedMonth, setSelectedMonth] = useState('all');
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedGSTRate, setSelectedGSTRate] = useState('5'); // Default 5%

  useEffect(() => {
    const db = getDatabase(app);
    const ordersRef = ref(db, 'HTAMS/orders');

    const unsubscribe = onValue(ordersRef, (snapshot) => {
      const ordersList = [];
      if (snapshot.exists()) {
        snapshot.forEach((yearSnapshot) => {
          // Check if this is a year node (2024, 2025, etc.) or direct order
          const yearKey = yearSnapshot.key;
          const yearData = yearSnapshot.val();

          if (yearData && typeof yearData === 'object') {
            // If it has order properties directly, it's an order
            if (yearData.orderDate || yearData.customerName || yearData.items) {
              ordersList.push({
                id: yearKey,
                ...yearData,
                orderDate: yearData.orderDate || yearData.createdAt || new Date().toISOString()
              });
            } else {
              // Otherwise, iterate through the children (year-based structure)
              Object.entries(yearData).forEach(([orderId, orderData]) => {
                if (orderData && typeof orderData === 'object') {
                  ordersList.push({
                    id: `${yearKey}_${orderId}`,
                    ...orderData,
                    orderDate: orderData.orderDate || orderData.createdAt || new Date().toISOString()
                  });
                }
              });
            }
          }
        });
      }
      setOrders(ordersList);
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  // Get available years and months
  const availableYears = useMemo(() => {
    const years = new Set();
    orders.forEach(order => {
      years.add(new Date(order.orderDate).getFullYear());
    });
    return Array.from(years).sort((a, b) => b - a);
  }, [orders]);

  const availableMonths = useMemo(() => {
    if (selectedYear === 'all') return [];
    const months = new Set();
    orders.forEach(order => {
      const date = new Date(order.orderDate);
      if (date.getFullYear() === parseInt(selectedYear)) {
        months.add(date.getMonth());
      }
    });
    return Array.from(months).sort((a, b) => a - b);
  }, [orders, selectedYear]);

  const filteredOrders = useMemo(() => {
    let filtered = orders.filter(order => order.status?.toLowerCase() !== 'cancelled');

    if (selectedYear !== 'all') {
      filtered = filtered.filter(order => {
        const orderDate = new Date(order.orderDate);
        const yearMatch = orderDate.getFullYear() === parseInt(selectedYear);

        if (selectedMonth === 'all') {
          return yearMatch;
        } else {
          return yearMatch && orderDate.getMonth() === parseInt(selectedMonth);
        }
      });
    }

    return filtered;
  }, [orders, selectedYear, selectedMonth]);


  const gstData = useMemo(() => {
    const gstEntries = [];
    let totalTaxableValue = 0;
    let totalCGST = 0;
    let totalSGST = 0;
    let totalIGST = 0;
    let totalTax = 0;

    filteredOrders.forEach(order => {
      if (order.items && Array.isArray(order.items)) {
        order.items.forEach(item => {
          // Calculate tax components
          const taxableValue = item.totalPrice || item.total || 0;

          // Use selected GST rate if specified, otherwise use item's rate or default to 5%
          const gstRate = selectedGSTRate !== 'all'
            ? parseFloat(selectedGSTRate)
            : (item.gstRate || 5);

          const totalGST = (taxableValue * gstRate) / (100 + gstRate);

          // Check if customer state is Maharashtra - if yes, use CGST+SGST, otherwise IGST
          const customerState = (order.customerAddress?.state || '').toLowerCase().trim();
          const isIntraState = customerState === 'maharashtra';
          const cgst = isIntraState ? totalGST / 2 : 0;
          const sgst = isIntraState ? totalGST / 2 : 0;
          const igst = isIntraState ? 0 : totalGST;

          const taxableAmount = taxableValue - totalGST;

          gstEntries.push({
            orderId: order.orderId || order.id,
            invoiceNumber: order.invoice?.invoiceNumber || 'N/A',
            invoiceDate: new Date(order.orderDate).toLocaleDateString('en-IN'),
            customerName: order.customerName || 'N/A',
            customerState: order.customerAddress?.state || 'N/A',
            productName: item.productName || item.name || 'N/A',
            quantity: item.quantity || 1,
            taxableValue: taxableAmount,
            gstRate: gstRate,
            cgst: cgst,
            sgst: sgst,
            igst: igst,
            totalTax: totalGST,
            totalValue: taxableValue
          });

          totalTaxableValue += taxableAmount;
          totalCGST += cgst;
          totalSGST += sgst;
          totalIGST += igst;
          totalTax += totalGST;
        });
      }
    });

    return {
      entries: gstEntries,
      summary: {
        totalInvoices: filteredOrders.length,
        totalTaxableValue,
        totalCGST,
        totalSGST,
        totalIGST,
        totalTax,
        totalValue: totalTaxableValue + totalTax
      }
    };
  }, [filteredOrders, selectedGSTRate]);

  const csvHeaders = [
    { label: 'Invoice Number', key: 'invoiceNumber' },
    { label: 'Invoice Date', key: 'invoiceDate' },
    { label: 'Customer Name', key: 'customerName' },
    { label: 'State', key: 'customerState' },
    { label: 'Product', key: 'productName' },
    { label: 'Quantity', key: 'quantity' },
    { label: 'Taxable Value', key: 'taxableValue' },
    { label: 'GST Rate %', key: 'gstRate' },
    { label: 'CGST', key: 'cgst' },
    { label: 'SGST', key: 'sgst' },
    { label: 'IGST', key: 'igst' },
    { label: 'Total Tax', key: 'totalTax' },
    { label: 'Total Value', key: 'totalValue' }
  ];

  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  const getReportTitle = () => {
    if (selectedYear === 'all') return 'GST Return - All Periods';
    if (selectedMonth === 'all') return `GST Return - ${selectedYear}`;
    return `GST Return - ${months[selectedMonth]} ${selectedYear}`;
  };

  // Prepare CSV data with totals row
  const csvDataWithTotals = useMemo(() => {
    const dataWithTotals = [...gstData.entries];

    // Add totals row at the end
    if (gstData.entries.length > 0) {
      dataWithTotals.push({
        invoiceNumber: '',
        invoiceDate: '',
        customerName: '',
        customerState: '',
        productName: 'TOTAL',
        quantity: '',
        taxableValue: gstData.summary.totalTaxableValue,
        gstRate: '',
        cgst: gstData.summary.totalCGST,
        sgst: gstData.summary.totalSGST,
        igst: gstData.summary.totalIGST,
        totalTax: gstData.summary.totalTax,
        totalValue: ''
      });
    }

    return dataWithTotals;
  }, [gstData]);

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '400px'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{
            width: '40px',
            height: '40px',
            border: '3px solid #e2e8f0',
            borderTopColor: '#3b82f6',
            borderRadius: '50%',
            animation: 'spin 0.8s linear infinite',
            margin: '0 auto 12px'
          }}></div>
          <p style={{ color: '#64748b', margin: 0 }}>Loading GST Data...</p>
        </div>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  return (
    <div className="gst-report">
      <div className="report-header">
        <div className="header-info">
          <h2><FaFileInvoice /> {getReportTitle()}</h2>
          <p>GST Return Summary and Details</p>
        </div>
        <CSVLink
          data={csvDataWithTotals}
          headers={csvHeaders}
          filename={`GST-Return-${selectedYear}-${selectedMonth !== 'all' ? months[selectedMonth] : 'All'}.csv`}
          className="export-btn"
        >
          <FaDownload /> Export to CSV
        </CSVLink>
      </div>

      {/* Filters */}
      <div className="filters">
        <div className="filter-group">
          <label>Financial Year</label>
          <select value={selectedYear} onChange={(e) => {
            setSelectedYear(e.target.value);
            setSelectedMonth('all');
          }}>
            <option value="all">All Years</option>
            {availableYears.map(year => (
              <option key={year} value={year}>{year}-{(year + 1).toString().slice(-2)}</option>
            ))}
          </select>
        </div>

        {selectedYear !== 'all' && (
          <div className="filter-group">
            <label>Month</label>
            <select value={selectedMonth} onChange={(e) => setSelectedMonth(e.target.value)}>
              <option value="all">All Months</option>
              {availableMonths.map(monthIndex => (
                <option key={monthIndex} value={monthIndex}>{months[monthIndex]}</option>
              ))}
            </select>
          </div>
        )}

        <div className="filter-group">
          <label>GST Rate</label>
          <select value={selectedGSTRate} onChange={(e) => setSelectedGSTRate(e.target.value)}>
            <option value="all">All Rates</option>
            <option value="0">0% (Nil Rated)</option>
            <option value="3">3%</option>
            <option value="5">5% (Default)</option>
            <option value="12">12%</option>
            <option value="18">18%</option>
            <option value="28">28%</option>
          </select>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="summary-grid">
        <div className="summary-card">
          <div className="summary-label">Total Invoices</div>
          <div className="summary-value">{gstData.summary.totalInvoices}</div>
        </div>
        <div className="summary-card">
          <div className="summary-label">Taxable Value</div>
          <div className="summary-value">₹{gstData.summary.totalTaxableValue.toLocaleString('en-IN', { maximumFractionDigits: 2 })}</div>
        </div>
        <div className="summary-card">
          <div className="summary-label">Total CGST</div>
          <div className="summary-value">₹{gstData.summary.totalCGST.toLocaleString('en-IN', { maximumFractionDigits: 2 })}</div>
        </div>
        <div className="summary-card">
          <div className="summary-label">Total SGST</div>
          <div className="summary-value">₹{gstData.summary.totalSGST.toLocaleString('en-IN', { maximumFractionDigits: 2 })}</div>
        </div>
        <div className="summary-card">
          <div className="summary-label">Total IGST</div>
          <div className="summary-value">₹{gstData.summary.totalIGST.toLocaleString('en-IN', { maximumFractionDigits: 2 })}</div>
        </div>
        <div className="summary-card highlight">
          <div className="summary-label">Total Tax</div>
          <div className="summary-value">₹{gstData.summary.totalTax.toLocaleString('en-IN', { maximumFractionDigits: 2 })}</div>
        </div>
      </div>

      {/* GST Entries Table */}
      <div className="table-container">
        <div className="table-header">GST Return Entries ({gstData.entries.length})</div>
        <div className="table-wrapper">
          <table>
            <thead>
              <tr>
                <th>Invoice No.</th>
                <th>Date</th>
                <th>Customer</th>
                <th>State</th>
                <th>Product</th>
                <th>Qty</th>
                <th>Taxable Value</th>
                <th>Rate %</th>
                <th>CGST</th>
                <th>SGST</th>
                <th>IGST</th>
                <th>Total Tax</th>
              </tr>
            </thead>
            <tbody>
              {gstData.entries.length > 0 ? (
                gstData.entries.map((entry, index) => (
                  <tr key={index}>
                    <td className="td-invoice">{entry.invoiceNumber}</td>
                    <td className="td-date">{entry.invoiceDate}</td>
                    <td>{entry.customerName}</td>
                    <td>{entry.customerState}</td>
                    <td className="td-product">{entry.productName}</td>
                    <td className="td-center">{entry.quantity}</td>
                    <td className="td-amount">₹{entry.taxableValue.toLocaleString('en-IN', { maximumFractionDigits: 2 })}</td>
                    <td className="td-center">{entry.gstRate}%</td>
                    <td className="td-tax">₹{entry.cgst.toLocaleString('en-IN', { maximumFractionDigits: 2 })}</td>
                    <td className="td-tax">₹{entry.sgst.toLocaleString('en-IN', { maximumFractionDigits: 2 })}</td>
                    <td className="td-tax">₹{entry.igst.toLocaleString('en-IN', { maximumFractionDigits: 2 })}</td>
                    <td className="td-total">₹{entry.totalTax.toLocaleString('en-IN', { maximumFractionDigits: 2 })}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="12" className="td-no-data">No GST entries found for selected period</td>
                </tr>
              )}
            </tbody>
            {gstData.entries.length > 0 && (
              <tfoot>
                <tr className="total-row">
                  <td colSpan="6" className="td-total-label">TOTAL</td>
                  <td className="td-amount-bold">₹{gstData.summary.totalTaxableValue.toLocaleString('en-IN', { maximumFractionDigits: 2 })}</td>
                  <td></td>
                  <td className="td-tax-bold">₹{gstData.summary.totalCGST.toLocaleString('en-IN', { maximumFractionDigits: 2 })}</td>
                  <td className="td-tax-bold">₹{gstData.summary.totalSGST.toLocaleString('en-IN', { maximumFractionDigits: 2 })}</td>
                  <td className="td-tax-bold">₹{gstData.summary.totalIGST.toLocaleString('en-IN', { maximumFractionDigits: 2 })}</td>
                  <td className="td-total-bold">₹{gstData.summary.totalTax.toLocaleString('en-IN', { maximumFractionDigits: 2 })}</td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>

      <style jsx>{`
        .gst-report {
          max-width: 100%;
          margin: 0 auto;
        }

        .report-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          background: white;
          padding: 16px 20px;
          border-radius: 6px;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
          margin-bottom: 16px;
        }

        .header-info h2 {
          margin: 0 0 4px 0;
          font-size: 1.4rem;
          font-weight: 700;
          color: #1e293b;
          display: flex;
          align-items: center;
          gap: 10px;
        }

        .header-info p {
          margin: 0;
          font-size: 0.85rem;
          color: #64748b;
        }

        .export-btn {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 10px 20px;
          background: #10b981;
          color: white;
          border-radius: 6px;
          font-size: 0.9rem;
          font-weight: 600;
          text-decoration: none;
          transition: all 0.2s;
        }

        .export-btn:hover {
          background: #059669;
          transform: translateY(-1px);
        }

        .filters {
          display: flex;
          gap: 16px;
          background: white;
          padding: 16px;
          border-radius: 6px;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
          margin-bottom: 16px;
        }

        .filter-group {
          flex: 1;
        }

        .filter-group label {
          display: block;
          font-size: 0.8rem;
          font-weight: 600;
          color: #475569;
          margin-bottom: 6px;
        }

        .filter-group select {
          width: 100%;
          padding: 8px 12px;
          border: 1px solid #e2e8f0;
          border-radius: 4px;
          font-size: 0.9rem;
          background: white;
          cursor: pointer;
        }

        .filter-group select:focus {
          outline: none;
          border-color: #3b82f6;
        }

        .summary-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 12px;
          margin-bottom: 16px;
        }

        .summary-card {
          background: white;
          padding: 16px;
          border-radius: 6px;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
        }

        .summary-card.highlight {
          background: linear-gradient(135deg, #3b82f6, #2563eb);
          color: white;
        }

        .summary-label {
          font-size: 0.75rem;
          font-weight: 700;
          color: #64748b;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          margin-bottom: 8px;
        }

        .summary-card.highlight .summary-label {
          color: rgba(255, 255, 255, 0.9);
        }

        .summary-value {
          font-size: 1.5rem;
          font-weight: 800;
          color: #1e293b;
        }

        .summary-card.highlight .summary-value {
          color: white;
        }

        .table-container {
          background: white;
          padding: 16px;
          border-radius: 6px;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
        }

        .table-header {
          font-size: 0.95rem;
          font-weight: 700;
          color: #1e293b;
          margin-bottom: 12px;
          padding-bottom: 10px;
          border-bottom: 2px solid #e2e8f0;
        }

        .table-wrapper {
          overflow-x: auto;
          border: 1px solid #e2e8f0;
          border-radius: 4px;
        }

        table {
          width: 100%;
          border-collapse: collapse;
          font-size: 0.8rem;
        }

        thead {
          background: #f8fafc;
        }

        th {
          text-align: left;
          padding: 10px 8px;
          font-weight: 700;
          color: #475569;
          font-size: 0.7rem;
          text-transform: uppercase;
          letter-spacing: 0.3px;
          border-bottom: 2px solid #e2e8f0;
          white-space: nowrap;
        }

        td {
          padding: 10px 8px;
          border-bottom: 1px solid #f1f5f9;
          color: #1e293b;
        }

        tbody tr:hover {
          background: #f8fafc;
        }

        tfoot {
          background: #f1f5f9;
          font-weight: 700;
        }

        .total-row td {
          border-top: 2px solid #cbd5e1;
          border-bottom: 2px solid #cbd5e1;
          padding: 12px 8px;
        }

        .td-invoice {
          font-family: monospace;
          color: #3b82f6;
          font-weight: 600;
        }

        .td-date {
          color: #64748b;
          font-size: 0.75rem;
        }

        .td-gstin {
          font-family: monospace;
          font-size: 0.75rem;
          color: #64748b;
        }

        .td-product {
          max-width: 200px;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        .td-hsn {
          font-family: monospace;
          font-size: 0.75rem;
        }

        .td-center {
          text-align: center;
        }

        .td-amount {
          text-align: right;
          font-weight: 600;
          color: #1e293b;
        }

        .td-tax {
          text-align: right;
          color: #f59e0b;
          font-weight: 500;
        }

        .td-total {
          text-align: right;
          color: #10b981;
          font-weight: 700;
        }

        .td-total-label {
          text-align: right;
          font-weight: 700;
          color: #1e293b;
        }

        .td-amount-bold {
          text-align: right;
          font-weight: 700;
          color: #1e293b;
        }

        .td-tax-bold {
          text-align: right;
          font-weight: 700;
          color: #f59e0b;
        }

        .td-total-bold {
          text-align: right;
          font-weight: 700;
          color: #10b981;
        }

        .td-no-data {
          text-align: center;
          padding: 40px !important;
          color: #94a3b8;
        }

        @media (max-width: 1024px) {
          .summary-grid {
            grid-template-columns: repeat(2, 1fr);
          }
        }

        @media (max-width: 768px) {
          .report-header {
            flex-direction: column;
            gap: 12px;
            align-items: flex-start;
          }

          .export-btn {
            width: 100%;
            justify-content: center;
          }

          .filters {
            flex-direction: column;
          }

          .summary-grid {
            grid-template-columns: 1fr;
          }
        }
      `}</style>
    </div>
  );
};

export default GSTReport;
